import { replaceSearchParams } from "@anna-money/anna-web-lib/utils";
import * as Sentry from "@sentry/browser";
import { observer } from "mobx-react-lite";
import { type FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorView } from "components/ErrorView";
import { Loader, LoaderMode } from "components/Loader/Loader";
import { useMount } from "helpers/hooks/useMount";
import { useReturnUrl } from "helpers/hooks/useReturnUrl";
import { useSearchParam } from "helpers/hooks/useSearchParam";
import { type BaseServices } from "services";
import { createAuthCallbackUrl } from "./authCallbackUrl";
import { type AuthSession } from "./authSessionStorage";
import { type AuthStore } from "./authStore";

const stateParam = "state";
const loginHintParamKey = "auth_login_hint";
const screenTypeParamKey = "auth_screen";
const gclidUrlParam = "gclid";
export const registerFormStepParam = "register_form_step";

// Logins started before the callback url became fixed come back with the destination in the query
// and the rest of the entry params in a single storage key, which the old capture page always wrote -
// its absence is what tells a real in-flight login from a callback url handed to us by someone else.
// Remove this branch once the old shape stops arriving in production (FlowFast 6818105).
const legacyRedirectParam = "redirect";
const legacyRedirectParamsKey = "redirect_url_params";

const rejectedCallbackCountKey = "auth_rejected_callbacks";
const maxRejectedCallbacks = 3;

const createLegacyCallbackUrl = (currentUrl: URL, legacyRedirect: string): URL => {
  const callbackUrl = new URL(currentUrl);
  replaceSearchParams(callbackUrl, new URLSearchParams({ [legacyRedirectParam]: legacyRedirect }));

  return callbackUrl;
};

const authenticate = async (authStore: AuthStore, currentUrl: URL, callbackUrl: URL): Promise<void> => {
  await authStore.authenticate(currentUrl, callbackUrl);
  if (authStore.state !== "authenticated") {
    throw new Error("Couldn't authenticate");
  }
};

const consumeSessionDestination = (authStore: AuthStore, sessionId: string, session: AuthSession): string => {
  authStore.removeSession(sessionId);

  return `${session.pathname}${session.search}${session.hash}`;
};

// Private and strict-privacy modes throw on session storage. A login that cannot keep state across
// the round trip cannot recover by restarting either, so such a browser goes straight to the error.
const readRejectedCallbacks = (): number => {
  try {
    return Number(sessionStorage.getItem(rejectedCallbackCountKey) ?? 0);
  } catch (e) {
    return maxRejectedCallbacks;
  }
};

const countRejectedCallback = (): number => {
  const rejectedCallbacks = readRejectedCallbacks() + 1;
  try {
    sessionStorage.setItem(rejectedCallbackCountKey, String(rejectedCallbacks));
  } catch (e) {
    return rejectedCallbacks;
  }

  return rejectedCallbacks;
};

const clearRejectedCallbacks = (): void => {
  try {
    sessionStorage.removeItem(rejectedCallbackCountKey);
  } catch (e) {
    Sentry.addBreadcrumb({ message: "session storage unavailable, rejected-callback counter not cleared" });
  }
};

export const createAuthorizeUrl = (authStore: AuthStore, currentUrl: URL): string => {
  const legacyRedirect = currentUrl.searchParams.get(legacyRedirectParam);
  const state =
    currentUrl.searchParams.get(stateParam) ??
    authStore.startSession({ pathname: legacyRedirect ?? "/", search: "", hash: "" });

  const authParams = getAuthParams(currentUrl.search);
  const loginHint = loginHintParamKey in authParams ? authParams[loginHintParamKey] : undefined;

  return authStore.getAuthUrl(createAuthCallbackUrl(currentUrl.origin), state, loginHint);
};

type AuthCapturePageProps = {
  services: BaseServices;
  redirect: string;
};
/**
 * Captures current (site entry) url and redirects to `AuthStartPage`
 * preserving current url if the user is unauthenticated
 */
export const AuthCapturePage: FC<AuthCapturePageProps> = observer(({ services, redirect }) => {
  const { authStore } = services;
  const navigate = useNavigate();
  const location = useLocation();
  useMount(() => {
    authStore.initialize();
    if (authStore.state !== "authenticated") {
      const authParams = getAuthParams(location.search);
      const state = authStore.startSession(location);
      navigate(
        {
          pathname: redirect,
          search: new URLSearchParams({
            ...authParams,
            [stateParam]: state,
          }).toString(),
        },
        { replace: true },
      );
    }
  });

  return <Loader size="xxl" mode={LoaderMode.Splashscreen} />;
});

type AuthStartPageProps = {
  services: BaseServices;
};

/**
 * Sends user to the authentication service
 */
export const AuthStartPage: FC<AuthStartPageProps> = ({ services }) => {
  const { authStore, analyticsManager } = services;

  useMount(() => {
    const authUrl = createAuthorizeUrl(authStore, new URL(window.location.toString()));
    analyticsManager.event("start-page.token-refresh");
    window.location.replace(authUrl);
  });

  return <Loader size="xxl" mode={LoaderMode.Splashscreen} />;
};

type AuthFinishPageProps = {
  services: BaseServices;
};

/**
 * Finalizes authenticating after coming from authentication service
 */
export const AuthFinishPage: FC<AuthFinishPageProps> = ({ services }) => {
  const navigate = useNavigate();
  const { authStore, dataStorage, analyticsManager } = services;
  const [failed, setFailed] = useState(false);

  useMount(async () => {
    const currentUrl = new URL(window.location.toString());
    const sessionId = currentUrl.searchParams.get(stateParam);
    const legacyRedirect = currentUrl.searchParams.get(legacyRedirectParam);
    const legacyEntryParams = dataStorage.get(legacyRedirectParamsKey, String);
    const isLegacyCallback = !sessionId && !!legacyRedirect && legacyEntryParams !== null;
    const session = sessionId ? authStore.loadSession(sessionId) : null;

    // The code belongs to a login this browser never started, so exchanging it would sign the user
    // into whoever did start it. Send them back and let the capture page start a login of their own,
    // unless we keep landing here - then the round trip is broken and looping helps nobody.
    if (!isLegacyCallback && !session) {
      const rejectedCallbacks = countRejectedCallback();
      analyticsManager.event("auth.callback-rejected", {
        reason: sessionId ? "unknown-state" : "no-state",
        rejectedCallbacks,
      });
      if (rejectedCallbacks >= maxRejectedCallbacks) {
        setFailed(true);
        return;
      }

      navigate("/", { replace: true });
      return;
    }

    const callbackUrl =
      isLegacyCallback && legacyRedirect
        ? createLegacyCallbackUrl(currentUrl, legacyRedirect)
        : createAuthCallbackUrl(currentUrl.origin);

    try {
      await authenticate(authStore, currentUrl, callbackUrl);
    } catch (e) {
      Sentry.captureException(e, { extra: { hasState: !!sessionId, isLegacyCallback } });
      analyticsManager.event("auth.callback-failed", { isLegacyCallback });
      setFailed(true);
      return;
    }

    clearRejectedCallbacks();

    if (isLegacyCallback && legacyRedirect) {
      dataStorage.remove(legacyRedirectParamsKey);
      navigate(`${legacyRedirect}${legacyEntryParams ?? ""}`, { replace: true });
      return;
    }

    navigate(sessionId && session ? consumeSessionDestination(authStore, sessionId, session) : "/", { replace: true });
  });

  if (failed) {
    return (
      <ErrorView
        retryTestId="auth-retry-button"
        onRetry={() => {
          window.location.replace("/");
        }}
      />
    );
  }

  return <div>Authenticating...</div>;
};

/**
 * Refreshing auth toke via redirect to the auth service
 */
export const AuthTokenRefreshPage: FC<AuthFinishPageProps> = ({ services }) => {
  const { authStore, analyticsManager } = services;
  const returnQuestion = useSearchParam(registerFormStepParam);
  const returnUrl = useReturnUrl(returnQuestion, true);
  useMount(async () => {
    const authUrl = authStore.getReturnAuthUrl(new URL(returnUrl));
    authStore.refresh();
    analyticsManager.event("auth.token-refresh");
    window.location.replace(authUrl);
  });

  return <div>Authenticating...</div>;
};

function getAuthParams(searchString: string): Record<string, string> {
  const result: Record<string, string> = {};
  const searchParams = new URLSearchParams(searchString);
  const loginHint = searchParams.get(loginHintParamKey);
  const screenType = searchParams.get(screenTypeParamKey);
  const gclid = searchParams.get(gclidUrlParam);

  if (loginHint) {
    result[loginHintParamKey] = loginHint;
  }
  if (screenType) {
    result[screenTypeParamKey] = screenType;
  }
  if (gclid) {
    result[gclidUrlParam] = gclid;
  }

  for (const [key, value] of searchParams.entries()) {
    if (key.toLowerCase().startsWith("utm_")) {
      result[key] = value;
    }
  }

  return result;
}
